import React, {useEffect, useState} from 'react';
import {withTranslation} from 'react-i18next';
import {ProgressBar} from 'primereact/progressbar';
import axios from 'axios';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Badge } from 'primereact/badge';

import ProductionMetricsMeasuresSummary from "../productionmetricsmeasuressummary/ProductionMetricsMeasuresSummary";
import ProductionWorkflowMetricsForm from "../productionmetricsmeasuressummary/ProductionWorkflowMetricsForm";

const ProductionitemCalendarListComponent = (props) => {
  const {t, onDetailedSearchActiveChange} = props;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draggingProductionItemIds, setDraggingProductionItemIds] = useState([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [detailedSearchVisible, setDetailedSearchVisible] = useState(false);
  const [detailedSearchQuery, setDetailedSearchQuery] = useState('');
  const isDetailedSearchActive = React.useMemo(() => {
    return (detailedSearchQuery || '').toString().trim().length > 0;
  }, [detailedSearchQuery]);
  const [dsName, setDsName] = useState('');
  const [dsPartnerName, setDsPartnerName] = useState('');
  const [dsProjectNumber, setDsProjectNumber] = useState('');
  const [dsProjectItemName, setDsProjectItemName] = useState('');
  const [productionStatus, setProductionStatus] = useState([]);

  const [metricsRefreshKey, setMetricsRefreshKey] = useState(0);

  const apiCall = (method, url, data, responseType) => {
    return axios({
      method,
      url: process.env.REACT_APP_API_HOST + url,
      data,
      params: {},
      responseType: responseType,
      headers: {
        'Authorization': 'Bearer ' + localStorage.getItem("userToken"),
        'App-Network': localStorage.getItem("networkId")
      }
    })
  }

  const loadData = (extraQuery = '') => {
    setLoading(true);
    apiCall('get', `/api/v2/production-item/calendar${extraQuery ? `?${extraQuery}` : ''}`)
      .then(response => {
        setItems(response?.data?.data?.items || []);
      })
      .catch(error => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
        setItems([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const buildDetailedSearchQuery = () => {
    let parts = [];
    if (dsName && dsName.trim() !== '') {
      parts.push(`search[name][operator]=like&search[name][value]=*${encodeURIComponent(dsName.trim())}*`);
    }
    if (dsPartnerName && dsPartnerName.trim() !== '') {
      parts.push(`search[partner_name][operator]=like&search[partner_name][value]=*${encodeURIComponent(dsPartnerName.trim())}*`);
    }
    if (dsProjectNumber && dsProjectNumber.trim() !== '') {
      parts.push(`search[project_number][operator]=like&search[project_number][value]=*${encodeURIComponent(dsProjectNumber.trim())}*`);
    }
    if (dsProjectItemName && dsProjectItemName.trim() !== '') {
      parts.push(`search[project_item_name][operator]=like&search[project_item_name][value]=*${encodeURIComponent(dsProjectItemName.trim())}*`);
    }
    return parts.join('&');
  };

  const handleDetailedSearch = () => {
    const q = buildDetailedSearchQuery();
    setDetailedSearchQuery(q);
    loadData(q);
  };

  const loadProductionItem = (id) => {
    setMetricsRefreshKey(0);
    if (!id) return;
    setDialogVisible(true);
    setDialogLoading(true);
    setSelectedItem(null);
    apiCall('get', `/api/v2/production-item/${id}`)
      .then((response) => {
        setSelectedItem(response?.data?.data || null);
      })
      .catch((error) => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
        setSelectedItem(null);
      })
      .finally(() => {
        setDialogLoading(false);
      });
  }

  const actionBodyTemplate = (rowData) => {
    return (
      <React.Fragment>
        {localStorage.getItem('roles').toString().includes('role_') &&
          <Button icon="pi pi-eye"
                  tooltip={t('view')} tooltipOptions={{position: 'left'}}
                  onClick={() => {
                    loadProductionItem(rowData?.id);
                  }}/>
        }
      </React.Fragment>
    );
  }

  const getProductionStatusSeverity = (status) => {
    const key = status ? status.toString().toUpperCase() : '';

    switch (key) {
      case 'NEW':
        return 'primary';
      case 'SCHEDULED':
        return 'secondary';
      case 'IN_PROGRESS':
        return 'info';
      case 'PAUSED':
      case 'SUSPENDED':
        return 'warning';
      case 'DONE':
        return 'success';
      case 'DELETED':
        return 'danger';
      default:
        return null;
    }
  };

  const getProductionStatusLabel = (status) => {
    if (!status) return '-';
    const key = status.toString().toUpperCase();
    const item = productionStatus.find((statusItem) => statusItem.key === key);

    return item?.value || key;
  };

  const renderProductionStatusBadge = (status) => {
    return (
      <Badge
        value={getProductionStatusLabel(status)}
        severity={getProductionStatusSeverity(status)}
      />
    );
  };

  const formatProjectItemName = (item) => {
    const projectItemName = item?.project_item?.name;
    const partnerName = item?.project_item?.project?.partner?.name;

    if (!projectItemName) return '-';

    return partnerName ? `${projectItemName} - ${partnerName}` : projectItemName;
  };

  useEffect(() => {
    loadData(detailedSearchQuery);
  }, [window.App.state.rerenderProductionitemCalendarList]);

  useEffect(() => {
    apiCall('get', '/api/v2/dictionary/production-status')
      .then((response) => {
        setProductionStatus(response?.data?.data || []);
      })
      .catch((error) => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });
  }, []);

  useEffect(() => {
    if (typeof onDetailedSearchActiveChange === 'function') {
      onDetailedSearchActiveChange(isDetailedSearchActive);
    }
  }, [isDetailedSearchActive, onDetailedSearchActiveChange]);

  const handleDragStart = (event, item) => {
    const payload = {
      source: 'production-item-calendar-list',
      id: item.id
    };
    setDraggingProductionItemIds([item.id]);
    event.dataTransfer.setData("application/json", JSON.stringify(payload));
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggingProductionItemIds([]);
    window.App.setState({
      dragOverScheduleItemId: null
    });
  };

  return (
    <div className={"ProductionitemCalendarListComponent"}>
      {loading && <ProgressBar mode="indeterminate" style={{height: '5px'}}/>}

      <div className={"p-d-flex p-jc-between p-ai-center p-mb-3"}>
        <h3 className={"p-m-0 text-primary"}>{t('productionOperations')}</h3>
        
        <div className={"p-col-12 p-lg-9 p-col-align-center p-text-center p-text-lg-right"}>
          <Button
            type="button"
            label={t('detailedSearch')}
            icon={isDetailedSearchActive ? "pi pi-filter-fill" : "pi pi-filter"}
            className="p-button-outlined p-mr-2"
            onClick={() => {
              setDetailedSearchVisible(!detailedSearchVisible);
              // if (!detailedSearchVisible) {
              //   loadFilterOptions();
              // }
            }}
          />
        </div>
      </div>
      {detailedSearchVisible &&
        <div className="p-mb-3"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleDetailedSearch();
          }
        }}>
          <div className="p-grid">
            <div className="p-col-12 p-lg-3">
              <div className="p-field p-fluid">
                <label>{t('fieldTask')}</label>
                <InputText
                  value={dsName}
                  onChange={(e) => setDsName(e.target.value)}
                />
              </div>
            </div>

            <div className="p-col-12 p-lg-3">
              <div className="p-field p-fluid">
                <label>{t('columnPartner')}</label>
                <InputText
                  value={dsPartnerName}
                  onChange={(e) => setDsPartnerName(e.target.value)}
                />
              </div>
            </div>

            <div className="p-col-12 p-lg-3">
              <div className="p-field p-fluid">
                <label>{t('columnProjectNumber')}</label>
                <InputText
                  value={dsProjectNumber}
                  onChange={(e) => setDsProjectNumber(e.target.value)}
                />
              </div>
            </div>

            <div className="p-col-12 p-lg-3">
              <div className="p-field p-fluid">
                <label>{t('columnItem')}</label>
                <InputText
                  value={dsProjectItemName}
                  onChange={(e) => setDsProjectItemName(e.target.value)}
                />
              </div>
            </div>

            <div className="p-col-12 p-text-right">
              <Button
                type="button"
                label={t('dsClearCriteria')}
                icon="pi pi-times"
                className="p-button-danger p-mr-2"
                onClick={() => {
                  setDsName('');
                  setDsPartnerName('');
                  setDsProjectNumber('');
                  setDsProjectItemName('');
                  setDetailedSearchQuery('');
                  loadData('');
                }}
              />
              <Button
                type="button"
                label={t('dsSearch')}
                icon="pi pi-search"
                className="p-button-primary"
                onClick={handleDetailedSearch}
              />
            </div>
          </div>
        </div>
      }

      {!loading && (!items || items.length === 0) &&
        <div className={"p-text-center p-p-4 p-text-secondary"}>
          {t('noResults')}
        </div>
      }

      <div className={"production-item-calendar-list"}>
        {(items || []).map((item) => (
          <div
            key={item.id}
            draggable={true}
            onDragStart={(e) => handleDragStart(e, item)}
            onDragEnd={handleDragEnd}
            className={
              "production-item-calendar-list-card p-mb-2 " +
              (draggingProductionItemIds.includes(item.id)
                ? 'dnd-drag-source-row-active'
                : 'dnd-drag-source-row')
            }
          >
            <div className={"production-item-calendar-list-card-inner p-d-flex p-jc-between p-ai-center"}>
              <div>
                <div className={"production-item-calendar-list-card-title"}>
                  {item.name || '-'}
                </div>
                <div className="p-mt-1">
                  {renderProductionStatusBadge(item?.status)}
                </div>
              </div>

              <div className={"production-item-calendar-list-card-meta p-text-center"}>
                <div>
                  <strong>{t('projectNumber')}:</strong> {item.project_number || '-'}
                </div>
                <div>
                  <strong>{t('quantity')}:</strong> {item.quantity || 0} {item?.unit?.name || ''}
                  <span className="p-text-secondary">
                    ({item.quantity_in_jobs || 0})
                  </span>
                </div>
                <div>
                  <strong>{t('projectItem')}:</strong> {formatProjectItemName(item)}
                </div>
              </div>

              <div>
                <div className={"production-item-calendar-list-card-title"}>
                  {actionBodyTemplate(item)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Dialog
        header={t('productionItemDetails')}
        visible={dialogVisible}
        style={{ width: '50vw' }}
        onHide={() => {
          setDialogVisible(false);
          setSelectedItem(null);
        }}
      >
        {dialogLoading && <ProgressBar mode="indeterminate" style={{height: '5px'}}/>}

        {!dialogLoading && selectedItem && (
          <>
            <div className="p-grid">
              <div className="p-col-12 p-lg-6">
                <div
                  className="p-p-3"
                  style={{
                    border: '1px solid var(--surface-d)',
                    borderRadius: 6,
                    height: '100%'
                  }}
                >
                  <h4 className="p-mt-0 p-mb-3 text-primary">
                    {t('projectItem')}
                  </h4>

                  <p>
                    <strong>{t('columnName')}:</strong>{' '}
                    {formatProjectItemName(selectedItem)}
                  </p>

                  <p>
                    <strong>{t('externalReference')}:</strong>{' '}
                    {selectedItem?.project_item?.external_reference || '-'}
                  </p>

                  <p>
                    <strong>{props.t('status')}:</strong>{' '}
                    {selectedItem?.project_item?.project_item_status?.name || '-'}
                  </p>

                  <p>
                    <strong>{t('itemQuantity')}:</strong>{' '}
                    {selectedItem?.project_item?.quantity || 0}{' '}
                    {selectedItem?.project_item?.unit?.name || ''}
                  </p>
                </div>
              </div>

              <div className="p-col-12 p-lg-6">
                <div
                  className="p-p-3"
                  style={{
                    border: '1px solid var(--surface-d)',
                    borderRadius: 6,
                    height: '100%'
                  }}
                >
                  <h4 className="p-mt-0 p-mb-3 text-primary">
                    {t('productionItem')}
                  </h4>

                  <p>
                    <strong>{t('technology')}:</strong>{' '}
                    {selectedItem?.name || '-'}
                  </p>

                  <p>
                    <strong>{t('projectNumber')}:</strong>{' '}
                    {selectedItem?.project_number || '-'}
                  </p>

                  <p>
                    <strong>{props.t('status')}:</strong>{' '}
                    {renderProductionStatusBadge(selectedItem?.status)}
                  </p>

                  <p>
                    <strong>{t('quantity')}:</strong>{' '}
                    {selectedItem?.quantity || 0}{' '}
                    {selectedItem?.unit?.name || ''}
                  </p>

                  <p>
                    <strong>{t('assignedQuantity')}:</strong>{' '}
                    {selectedItem?.quantity_in_jobs || 0}{' '}
                    {selectedItem?.unit?.name || ''}
                  </p>
                </div>
              </div>
            </div>

            {/* <div className="p-mt-3">
              <h4 className="text-primary">
                Összesített termelési adatok
              </h4>
            </div> */}
          </>
        )}
        <ProductionMetricsMeasuresSummary
          projectItemId={selectedItem?.project_item?.id}
          refreshKey={metricsRefreshKey}
          t={t}
        />

        <ProductionWorkflowMetricsForm
          projectItemId={selectedItem?.project_item?.id}
          productionItemId={selectedItem?.id}
          t={t}
          onSaved={() => setMetricsRefreshKey((prev) => prev + 1)}
        />
        <Button icon="pi pi-download" className="p-button-secondary p-mr-2"
                tooltip={t('download')} tooltipOptions={{position: 'left'}}
                onClick={() => {
                  if (!selectedItem) return;
                  apiCall('get', `/api/v2/project-item/${selectedItem.id}/download/pdf`, null, 'blob')
                    .then((response) => {
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `project_item_${selectedItem.id}.pdf`);
                      document.body.appendChild(link);
                      link.click();
                    })
                    .catch((error) => {
                      window.App.toastShow('error', t('error'), error.toString(), '', error.response);
                    });
                }}
        ></Button>
      </Dialog>
    </div>
  )
}

export default withTranslation('common')(ProductionitemCalendarListComponent);
